export interface CityData {
  slug: string;
  name: string;
  province: string;
  provinceCode: string;
  population: number;
  avgSnowfall: number; // in cm
  marketSize: string;
  demographics: {
    avgAge: number;
    homeownershipRate: number;
    avgHouseholdIncome: number;
  };
  keyNeighborhoods: string[];
  seasonalNotes: string;
  metaDescription: string;
  uniqueSellPoints: string[];
}

export const canadianCities: CityData[] = [
  // Ontario
  {
    slug: 'toronto',
    name: 'Toronto',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 2930000,
    avgSnowfall: 108,
    marketSize: 'Major Metropolitan',
    demographics: {
      avgAge: 39,
      homeownershipRate: 67,
      avgHouseholdIncome: 81000
    },
    keyNeighborhoods: ['North York', 'Etobicoke', 'Scarborough', 'East York', 'Yorkville', 'The Beaches', 'High Park'],
    seasonalNotes: 'Moderate snowfall with occasional heavy storms. Urban density requires quick clearing.',
    metaDescription: 'Get reliable, on-demand snow removal in Toronto with SNOWBLOWR. Local operators clear your driveway in 2 hours. Transparent pricing, no contracts. Download the app now!',
    uniqueSellPoints: [
      'Over 200 verified operators across the GTA',
      'Average 2-hour service time',
      'Serving all Toronto neighborhoods from downtown to suburbs',
      'Bilingual support for diverse communities'
    ]
  },
  {
    slug: 'ottawa',
    name: 'Ottawa',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 1017000,
    avgSnowfall: 223,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 68,
      avgHouseholdIncome: 92000
    },
    keyNeighborhoods: ['Kanata', 'Barrhaven', 'Orleans', 'Nepean', 'Glebe', 'Westboro', 'Stittsville'],
    seasonalNotes: 'Highest snowfall among major Canadian cities. Long winter season from November to April.',
    metaDescription: 'SNOWBLOWR in Ottawa - Your on-demand snow removal solution for Canada\'s snowiest capital. Verified local operators, 2-hour service, transparent pricing. Book now!',
    uniqueSellPoints: [
      'Purpose-built for Ottawa\'s heavy snowfall',
      '150+ operators across all quadrants',
      'Bilingual service (English/French)',
      'Proven through 200+ cm annual snowfall seasons'
    ]
  },
  {
    slug: 'mississauga',
    name: 'Mississauga',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 721000,
    avgSnowfall: 108,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 38,
      homeownershipRate: 72,
      avgHouseholdIncome: 87000
    },
    keyNeighborhoods: ['Port Credit', 'Streetsville', 'Meadowvale', 'Erin Mills', 'Clarkson', 'Malton'],
    seasonalNotes: 'Similar patterns to Toronto. High suburban density with many detached homes.',
    metaDescription: 'On-demand snow removal in Mississauga with SNOWBLOWR. Fast, reliable driveway clearing by verified local operators. No contracts, transparent pricing. Get the app!',
    uniqueSellPoints: [
      '100+ operators serving Mississauga',
      'Focus on suburban single-family homes',
      'Integrated with Toronto region network',
      'Average $42 per driveway clear'
    ]
  },
  {
    slug: 'brampton',
    name: 'Brampton',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 656000,
    avgSnowfall: 108,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 35,
      homeownershipRate: 75,
      avgHouseholdIncome: 79000
    },
    keyNeighborhoods: ['Bramalea', 'Heart Lake', 'Sandalwood', 'Springdale', 'Queen Street Corridor'],
    seasonalNotes: 'Fast-growing suburban market with high homeownership rates.',
    metaDescription: 'SNOWBLOWR brings fast, affordable snow removal to Brampton. Book verified local operators in minutes. No contracts, fair pricing, 2-hour service. Download now!',
    uniqueSellPoints: [
      'Fastest growing coverage area',
      '80+ operators in Brampton and Peel Region',
      'Competitive pricing for large driveways',
      'Family-friendly service approach'
    ]
  },
  {
    slug: 'hamilton',
    name: 'Hamilton',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 569000,
    avgSnowfall: 126,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 70,
      avgHouseholdIncome: 76000
    },
    keyNeighborhoods: ['Ancaster', 'Dundas', 'Waterdown', 'Stoney Creek', 'Westdale', 'Mountain'],
    seasonalNotes: 'Lake effect snow from Lake Ontario. Variable conditions across escarpment.',
    metaDescription: 'Hamilton snow removal made easy with SNOWBLOWR. On-demand clearing by local operators across the mountain and lower city. Transparent pricing, no BS. Book now!',
    uniqueSellPoints: [
      'Escarpment-aware pricing and routing',
      '60+ operators across Hamilton regions',
      'Lake effect snow specialists',
      'Same-storm service guarantee'
    ]
  },
  {
    slug: 'london',
    name: 'London',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 423000,
    avgSnowfall: 194,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 68,
      avgHouseholdIncome: 75000
    },
    keyNeighborhoods: ['Byron', 'Oakridge', 'White Oaks', 'Masonville', 'Old East Village', 'Wortley Village'],
    seasonalNotes: 'Significant snowfall with frequent winter storms off the Great Lakes.',
    metaDescription: 'On-demand snow removal in London, ON with SNOWBLOWR. Fast, reliable service by verified operators. No seasonal contracts, transparent pricing. Get started today!',
    uniqueSellPoints: [
      '50+ operators serving London region',
      'University district specialists',
      'Strong community operator network',
      'Competitive rates for forest city residents'
    ]
  },
  {
    slug: 'markham',
    name: 'Markham',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 350000,
    avgSnowfall: 108,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 41,
      homeownershipRate: 82,
      avgHouseholdIncome: 105000
    },
    keyNeighborhoods: ['Unionville', 'Markham Village', 'Thornhill', 'Cornell', 'Berczy Village'],
    seasonalNotes: 'Affluent suburban market with high homeownership and large properties.',
    metaDescription: 'Premium snow removal in Markham with SNOWBLOWR. Verified operators for your driveway and walkways. Fast service, transparent pricing, no contracts. Download the app!',
    uniqueSellPoints: [
      'Premium service for upscale properties',
      '40+ operators in York Region',
      'Large driveway specialists',
      'Integrated Toronto network coverage'
    ]
  },
  {
    slug: 'vaughan',
    name: 'Vaughan',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 323000,
    avgSnowfall: 108,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 84,
      avgHouseholdIncome: 103000
    },
    keyNeighborhoods: ['Woodbridge', 'Thornhill', 'Maple', 'Concord', 'Kleinburg'],
    seasonalNotes: 'High-end suburban market with large properties and driveways.',
    metaDescription: 'SNOWBLOWR in Vaughan - On-demand snow removal for your driveway. Verified local operators, 2-hour service, fair pricing. No contracts. Book your first clear now!',
    uniqueSellPoints: [
      'Large property specialists',
      '35+ operators in Vaughan area',
      'Premium equipment and service',
      'York Region leader in on-demand snow removal'
    ]
  },
  {
    slug: 'kitchener',
    name: 'Kitchener',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 256000,
    avgSnowfall: 140,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 39,
      homeownershipRate: 65,
      avgHouseholdIncome: 78000
    },
    keyNeighborhoods: ['Doon', 'Forest Heights', 'Beechwood', 'Stanley Park', 'Victoria Hills'],
    seasonalNotes: 'Tech hub with young professionals. Part of Waterloo Region with consistent snowfall.',
    metaDescription: 'Fast snow removal in Kitchener with SNOWBLOWR. Book verified operators on-demand. Transparent pricing, no contracts, 2-hour service. Perfect for tech-savvy homeowners!',
    uniqueSellPoints: [
      'Tech-forward service for innovation corridor',
      '30+ operators in K-W region',
      'Waterloo Region coverage',
      'Student-friendly pricing and service'
    ]
  },
  {
    slug: 'windsor',
    name: 'Windsor',
    province: 'Ontario',
    provinceCode: 'ON',
    population: 229000,
    avgSnowfall: 111,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 42,
      homeownershipRate: 69,
      avgHouseholdIncome: 73000
    },
    keyNeighborhoods: ['Walkerville', 'Riverside', 'South Windsor', 'East Windsor', 'Sandwich Town'],
    seasonalNotes: 'Moderate snowfall with lake effect patterns. Cross-border trade community.',
    metaDescription: 'Windsor snow removal on-demand with SNOWBLOWR. Local operators clear your driveway fast. No contracts, transparent pricing, verified service. Download today!',
    uniqueSellPoints: [
      'Border city snow removal specialists',
      '25+ operators serving Windsor-Essex',
      'Competitive pricing for the region',
      'Lake effect snow experts'
    ]
  },

  // Quebec
  {
    slug: 'montreal',
    name: 'Montreal',
    province: 'Quebec',
    provinceCode: 'QC',
    population: 1780000,
    avgSnowfall: 210,
    marketSize: 'Major Metropolitan',
    demographics: {
      avgAge: 39,
      homeownershipRate: 55,
      avgHouseholdIncome: 77000
    },
    keyNeighborhoods: ['Westmount', 'Outremont', 'NDG', 'Plateau', 'Rosemont', 'Verdun', 'LaSalle', 'Laval'],
    seasonalNotes: 'Heavy snowfall with strict municipal clearing rules. Bilingual market essential.',
    metaDescription: 'Déneigement à Montréal avec SNOWBLOWR. Service à la demande, opérateurs vérifiés, prix transparents. Snow removal in Montreal made easy. Book now!',
    uniqueSellPoints: [
      'Service bilingue complet / Full bilingual service',
      '200+ opérateurs / operators across Greater Montreal',
      'Complies with city snow removal bylaws',
      'Island and off-island coverage'
    ]
  },
  {
    slug: 'quebec-city',
    name: 'Quebec City',
    province: 'Quebec',
    provinceCode: 'QC',
    population: 549000,
    avgSnowfall: 316,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 42,
      homeownershipRate: 60,
      avgHouseholdIncome: 79000
    },
    keyNeighborhoods: ['Sainte-Foy', 'Charlesbourg', 'Beauport', 'Limoilou', 'Cap-Rouge', 'Sillery'],
    seasonalNotes: 'Highest snowfall of major Canadian cities. Long, intense winter season.',
    metaDescription: 'Déneigement à Québec avec SNOWBLOWR - Service rapide et fiable. Opérateurs locaux vérifiés, tarifs clairs. Snow removal in Quebec City. Téléchargez maintenant!',
    uniqueSellPoints: [
      'Built for Quebec City\'s extreme snowfall',
      '100+ opérateurs locaux',
      'Service français prioritaire',
      'Experts en déneigement résidentiel'
    ]
  },
  {
    slug: 'laval',
    name: 'Laval',
    province: 'Quebec',
    provinceCode: 'QC',
    population: 438000,
    avgSnowfall: 210,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 41,
      homeownershipRate: 72,
      avgHouseholdIncome: 82000
    },
    keyNeighborhoods: ['Chomedey', 'Vimont', 'Pont-Viau', 'Sainte-Rose', 'Fabreville', 'Auteuil'],
    seasonalNotes: 'Suburban market with high homeownership. Part of Greater Montreal network.',
    metaDescription: 'Déneigement à Laval avec SNOWBLOWR. Service à domicile rapide et abordable. Opérateurs vérifiés, sans contrat. Snow removal on-demand. Réservez maintenant!',
    uniqueSellPoints: [
      'Coverage across all Laval sectors',
      '60+ opérateurs à Laval',
      'Integrated with Montreal network',
      'Suburban driveway specialists'
    ]
  },
  {
    slug: 'gatineau',
    name: 'Gatineau',
    province: 'Quebec',
    provinceCode: 'QC',
    population: 291000,
    avgSnowfall: 223,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 66,
      avgHouseholdIncome: 85000
    },
    keyNeighborhoods: ['Hull', 'Aylmer', 'Buckingham', 'Masson-Angers', 'Gatineau Sector'],
    seasonalNotes: 'NCR market with federal employee base. Mirrors Ottawa snowfall patterns.',
    metaDescription: 'Déneigement à Gatineau avec SNOWBLOWR. Service bilingue, opérateurs locaux, prix clairs. Snow removal in Gatineau on-demand. Book your clear now!',
    uniqueSellPoints: [
      'National Capital Region coverage',
      'Fully bilingual operators',
      '50+ opérateurs in Gatineau region',
      'Cross-river coordination with Ottawa'
    ]
  },
  {
    slug: 'longueuil',
    name: 'Longueuil',
    province: 'Quebec',
    provinceCode: 'QC',
    population: 246000,
    avgSnowfall: 210,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 42,
      homeownershipRate: 65,
      avgHouseholdIncome: 76000
    },
    keyNeighborhoods: ['Vieux-Longueuil', 'Saint-Hubert', 'Greenfield Park', 'Le Moyne', 'Boucherville'],
    seasonalNotes: 'South Shore market. Family-oriented suburbs with strong homeowner base.',
    metaDescription: 'Déneigement à Longueuil avec SNOWBLOWR. Service rapide sur la Rive-Sud. Opérateurs vérifiés, sans contrat. Snow removal made simple. Téléchargez l\'app!',
    uniqueSellPoints: [
      'South Shore specialists',
      '40+ opérateurs on Rive-Sud',
      'Quick access via bridges',
      'Family-focused service'
    ]
  },
  {
    slug: 'sherbrooke',
    name: 'Sherbrooke',
    province: 'Quebec',
    provinceCode: 'QC',
    population: 172000,
    avgSnowfall: 245,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 41,
      homeownershipRate: 58,
      avgHouseholdIncome: 68000
    },
    keyNeighborhoods: ['Fleurimont', 'Lennoxville', 'Rock Forest', 'Jacques-Cartier', 'Mont-Bellevue'],
    seasonalNotes: 'Eastern Townships. Heavy snowfall, hilly terrain, university community.',
    metaDescription: 'Déneigement à Sherbrooke avec SNOWBLOWR. Service à la demande pour les Cantons-de-l\'Est. Opérateurs locaux, prix transparents. Book snow removal now!',
    uniqueSellPoints: [
      'Eastern Townships leader',
      '25+ local operators',
      'Bilingual university community service',
      'Terrain-adapted pricing and service'
    ]
  },

  // British Columbia
  {
    slug: 'vancouver',
    name: 'Vancouver',
    province: 'British Columbia',
    provinceCode: 'BC',
    population: 662000,
    avgSnowfall: 35,
    marketSize: 'Major Metropolitan',
    demographics: {
      avgAge: 40,
      homeownershipRate: 65,
      avgHouseholdIncome: 98000
    },
    keyNeighborhoods: ['Kitsilano', 'Kerrisdale', 'Dunbar', 'Point Grey', 'East Van', 'Commercial Drive'],
    seasonalNotes: 'Light snowfall but city grinds to halt when it snows. High demand, limited supply.',
    metaDescription: 'Snow removal in Vancouver with SNOWBLOWR. Rare snow? We\'re ready. On-demand clearing by local operators. No panic, just service. Download the app now!',
    uniqueSellPoints: [
      'Always ready for rare Vancouver snow',
      '80+ operators across Metro Vancouver',
      'Premium pricing reflects scarcity',
      'Quick response when the city needs it'
    ]
  },
  {
    slug: 'surrey',
    name: 'Surrey',
    province: 'British Columbia',
    provinceCode: 'BC',
    population: 568000,
    avgSnowfall: 35,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 37,
      homeownershipRate: 70,
      avgHouseholdIncome: 84000
    },
    keyNeighborhoods: ['Cloverdale', 'Guildford', 'Fleetwood', 'Newton', 'South Surrey', 'Whalley'],
    seasonalNotes: 'Similar to Vancouver. Fast-growing suburban market.',
    metaDescription: 'On-demand snow removal in Surrey, BC with SNOWBLOWR. When rare snow hits, we deliver fast clearing. Verified operators, transparent pricing. Get the app!',
    uniqueSellPoints: [
      'Largest operator network in Fraser Valley',
      '60+ operators across Surrey',
      'Suburban snow specialists',
      'Fast-growing coverage area'
    ]
  },
  {
    slug: 'burnaby',
    name: 'Burnaby',
    province: 'British Columbia',
    provinceCode: 'BC',
    population: 249000,
    avgSnowfall: 35,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 41,
      homeownershipRate: 68,
      avgHouseholdIncome: 89000
    },
    keyNeighborhoods: ['Metrotown', 'Edmonds', 'Brentwood', 'Capitol Hill', 'Deer Lake'],
    seasonalNotes: 'Metro Vancouver core. Mix of high-rises and single-family homes.',
    metaDescription: 'Burnaby snow removal on-demand with SNOWBLOWR. Rare snowfall coverage when you need it most. Local operators, transparent pricing. Download today!',
    uniqueSellPoints: [
      'Central Metro Vancouver location',
      '30+ operators in Burnaby',
      'Mixed residential specialists',
      'Integrated Vancouver network'
    ]
  },
  {
    slug: 'victoria',
    name: 'Victoria',
    province: 'British Columbia',
    provinceCode: 'BC',
    population: 92000,
    avgSnowfall: 25,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 43,
      homeownershipRate: 64,
      avgHouseholdIncome: 86000
    },
    keyNeighborhoods: ['James Bay', 'Oak Bay', 'Fairfield', 'Fernwood', 'Saanich'],
    seasonalNotes: 'Very rare snowfall but city unprepared. High urgency when it happens.',
    metaDescription: 'Victoria snow removal with SNOWBLOWR. When rare snow strikes the island, we respond fast. On-demand clearing, verified operators. Be ready - download now!',
    uniqueSellPoints: [
      'Island snow removal specialists',
      '20+ operators on Vancouver Island',
      'Emergency response focused',
      'Greater Victoria and Saanich coverage'
    ]
  },
  {
    slug: 'kelowna',
    name: 'Kelowna',
    province: 'British Columbia',
    provinceCode: 'BC',
    population: 144000,
    avgSnowfall: 104,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 44,
      homeownershipRate: 71,
      avgHouseholdIncome: 79000
    },
    keyNeighborhoods: ['Glenmore', 'Rutland', 'Mission', 'South Pandosy', 'North Glenmore'],
    seasonalNotes: 'Interior BC. Moderate but consistent snowfall. Growing retirement community.',
    metaDescription: 'Kelowna snow removal made easy with SNOWBLOWR. Okanagan\'s on-demand clearing service. Local operators, fair pricing, no contracts. Perfect for the valley!',
    uniqueSellPoints: [
      'Okanagan Valley leader',
      '25+ operators in Kelowna region',
      'Retirement community friendly',
      'Lake and hillside property specialists'
    ]
  },

  // Alberta
  {
    slug: 'calgary',
    name: 'Calgary',
    province: 'Alberta',
    provinceCode: 'AB',
    population: 1306000,
    avgSnowfall: 128,
    marketSize: 'Major Metropolitan',
    demographics: {
      avgAge: 37,
      homeownershipRate: 72,
      avgHouseholdIncome: 104000
    },
    keyNeighborhoods: ['Beltline', 'Kensington', 'Marda Loop', 'Bridgeland', 'Mount Royal', 'Inglewood'],
    seasonalNotes: 'Chinook winds create variable conditions. High income market with service expectations.',
    metaDescription: 'Calgary snow removal on-demand with SNOWBLOWR. Chinook or blizzard, we clear your driveway fast. Verified operators, transparent pricing. Download the app now!',
    uniqueSellPoints: [
      'Chinook-adapted service and pricing',
      '150+ operators across Calgary',
      'Premium market with premium service',
      'All quadrants covered'
    ]
  },
  {
    slug: 'edmonton',
    name: 'Edmonton',
    province: 'Alberta',
    provinceCode: 'AB',
    population: 1010000,
    avgSnowfall: 123,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 37,
      homeownershipRate: 69,
      avgHouseholdIncome: 98000
    },
    keyNeighborhoods: ['Oliver', 'Whyte Avenue', 'Garneau', 'Riverbend', 'Summerside', 'Castle Downs'],
    seasonalNotes: 'Longer, colder winters than Calgary. Consistent snowfall throughout season.',
    metaDescription: 'Edmonton snow removal with SNOWBLOWR. On-demand clearing for Alberta\'s capital. Local operators, 2-hour service, no contracts. Book your first clear now!',
    uniqueSellPoints: [
      'Built for Edmonton winters',
      '120+ operators city-wide',
      'Long-season specialists',
      'Extreme cold service capability'
    ]
  },
  {
    slug: 'red-deer',
    name: 'Red Deer',
    province: 'Alberta',
    provinceCode: 'AB',
    population: 100000,
    avgSnowfall: 112,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 36,
      homeownershipRate: 70,
      avgHouseholdIncome: 91000
    },
    keyNeighborhoods: ['Oriole Park', 'Westlake', 'Timberlands', 'Riverside Meadows'],
    seasonalNotes: 'Central Alberta hub. Family-oriented market with new developments.',
    metaDescription: 'Red Deer snow removal on-demand with SNOWBLOWR. Central Alberta\'s trusted clearing service. Local operators, fair pricing, fast service. Download today!',
    uniqueSellPoints: [
      'Central Alberta leader',
      '15+ local operators',
      'Family-focused service',
      'Competitive regional pricing'
    ]
  },
  {
    slug: 'lethbridge',
    name: 'Lethbridge',
    province: 'Alberta',
    provinceCode: 'AB',
    population: 101000,
    avgSnowfall: 124,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 38,
      homeownershipRate: 68,
      avgHouseholdIncome: 84000
    },
    keyNeighborhoods: ['West Lethbridge', 'North Lethbridge', 'South Side', 'Copperwood'],
    seasonalNotes: 'Southern Alberta with strong winds. Chinook effects and blowing snow challenges.',
    metaDescription: 'Lethbridge snow removal with SNOWBLOWR. Southern Alberta on-demand clearing. Wind and snow specialists, verified operators. Book your clear now!',
    uniqueSellPoints: [
      'Wind and snow specialists',
      '12+ local operators',
      'Southern Alberta coverage',
      'University community service'
    ]
  },

  // Manitoba
  {
    slug: 'winnipeg',
    name: 'Winnipeg',
    province: 'Manitoba',
    provinceCode: 'MB',
    population: 749000,
    avgSnowfall: 114,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 67,
      avgHouseholdIncome: 80000
    },
    keyNeighborhoods: ['River Heights', 'Wolseley', 'Transcona', 'St. Vital', 'Charleswood', 'Osborne Village'],
    seasonalNotes: 'Extreme cold with moderate snowfall. Long winter season, strong community ties.',
    metaDescription: 'Winnipeg snow removal on-demand with SNOWBLOWR. Prairie winter specialists. Verified local operators, transparent pricing, reliable service. Download the app!',
    uniqueSellPoints: [
      'Extreme cold specialists',
      '80+ operators across Winnipeg',
      'Community-focused service',
      'Long-season reliability'
    ]
  },

  // Saskatchewan
  {
    slug: 'saskatoon',
    name: 'Saskatoon',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    population: 278000,
    avgSnowfall: 93,
    marketSize: 'Mid-Major Urban',
    demographics: {
      avgAge: 38,
      homeownershipRate: 68,
      avgHouseholdIncome: 88000
    },
    keyNeighborhoods: ['Nutana', 'City Park', 'Riversdale', 'Stonebridge', 'Willowgrove'],
    seasonalNotes: 'Prairie climate with blowing snow. Strong community values and local business support.',
    metaDescription: 'Saskatoon snow removal with SNOWBLOWR. On-demand clearing for Saskatchewan homes. Local operators, prairie-tough service, fair pricing. Book now!',
    uniqueSellPoints: [
      'Prairie snow specialists',
      '30+ local operators',
      'Community-driven network',
      'Blowing snow expertise'
    ]
  },
  {
    slug: 'regina',
    name: 'Regina',
    province: 'Saskatchewan',
    provinceCode: 'SK',
    population: 226000,
    avgSnowfall: 104,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 39,
      homeownershipRate: 67,
      avgHouseholdIncome: 86000
    },
    keyNeighborhoods: ['Cathedral', 'Lakeview', 'Harbour Landing', 'Wascana View', 'Rosemont'],
    seasonalNotes: 'Saskatchewan capital. Extreme winter conditions, strong homeowner market.',
    metaDescription: 'Regina snow removal on-demand with SNOWBLOWR. Capital city clearing service. Verified operators, transparent pricing, prairie reliability. Download today!',
    uniqueSellPoints: [
      'Capital city coverage',
      '25+ verified operators',
      'Extreme weather ready',
      'Government district specialists'
    ]
  },

  // Nova Scotia
  {
    slug: 'halifax',
    name: 'Halifax',
    province: 'Nova Scotia',
    provinceCode: 'NS',
    population: 439000,
    avgSnowfall: 156,
    marketSize: 'Major Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 64,
      avgHouseholdIncome: 79000
    },
    keyNeighborhoods: ['South End', 'North End', 'Clayton Park', 'Dartmouth', 'Bedford', 'Sackville'],
    seasonalNotes: 'Maritime snowfall with nor\'easters. Variable conditions, rapid storm systems.',
    metaDescription: 'Halifax snow removal with SNOWBLOWR. Maritime snow specialists. On-demand clearing by local operators. Nor\'easter ready. Download the app now!',
    uniqueSellPoints: [
      'Maritime weather specialists',
      '50+ operators across HRM',
      'Nor\'easter response experts',
      'Halifax, Dartmouth, Bedford coverage'
    ]
  },

  // New Brunswick
  {
    slug: 'moncton',
    name: 'Moncton',
    province: 'New Brunswick',
    provinceCode: 'NB',
    population: 85000,
    avgSnowfall: 228,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 42,
      homeownershipRate: 66,
      avgHouseholdIncome: 73000
    },
    keyNeighborhoods: ['Downtown Moncton', 'Riverview', 'Dieppe', 'Magnetic Hill', 'Mapleton'],
    seasonalNotes: 'Heavy Maritime snowfall. Bilingual market, strong community connections.',
    metaDescription: 'Moncton / Dieppe snow removal with SNOWBLOWR. Déneigement à la demande. Bilingual service, local operators. Heavy snow specialists. Book now / Réservez!',
    uniqueSellPoints: [
      'Fully bilingual service',
      'Heavy snowfall specialists',
      '15+ local operators',
      'Greater Moncton coverage'
    ]
  },
  {
    slug: 'fredericton',
    name: 'Fredericton',
    province: 'New Brunswick',
    provinceCode: 'NB',
    population: 63000,
    avgSnowfall: 230,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 41,
      homeownershipRate: 65,
      avgHouseholdIncome: 75000
    },
    keyNeighborhoods: ['Downtown', 'Nashwaaksis', 'Skyline Acres', 'Devon', 'Marysville'],
    seasonalNotes: 'Provincial capital with heavy snowfall. Government and university community.',
    metaDescription: 'Fredericton snow removal with SNOWBLOWR. Capital city on-demand clearing. Bilingual operators, transparent pricing, heavy snow experts. Download today!',
    uniqueSellPoints: [
      'Capital city specialists',
      '12+ local operators',
      'Bilingual government community',
      'River valley snow experts'
    ]
  },
  {
    slug: 'saint-john',
    name: 'Saint John',
    province: 'New Brunswick',
    provinceCode: 'NB',
    population: 68000,
    avgSnowfall: 220,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 43,
      homeownershipRate: 63,
      avgHouseholdIncome: 71000
    },
    keyNeighborhoods: ['Uptown', 'East Saint John', 'West Side', 'Millidgeville', 'South End'],
    seasonalNotes: 'Bay of Fundy effects. Variable Maritime conditions, community-focused.',
    metaDescription: 'Saint John snow removal on-demand with SNOWBLOWR. Port city snow specialists. Local operators, bilingual service, Maritime reliability. Book your clear!',
    uniqueSellPoints: [
      'Bay of Fundy weather specialists',
      '12+ local operators',
      'Historic city snow clearing',
      'Community-driven service'
    ]
  },

  // Newfoundland
  {
    slug: 'st-johns',
    name: 'St. John\'s',
    province: 'Newfoundland and Labrador',
    provinceCode: 'NL',
    population: 114000,
    avgSnowfall: 335,
    marketSize: 'Mid Urban',
    demographics: {
      avgAge: 40,
      homeownershipRate: 72,
      avgHouseholdIncome: 92000
    },
    keyNeighborhoods: ['Downtown', 'Mount Pearl', 'Paradise', 'CBS', 'Torbay', 'Quidi Vidi'],
    seasonalNotes: 'Highest snowfall in Canada for major city. Extreme storms, strong community resilience.',
    metaDescription: 'St. John\'s snow removal with SNOWBLOWR. Built for Canada\'s snowiest city. On-demand clearing by local operators. Storm-proof service. Download now!',
    uniqueSellPoints: [
      'Built for extreme snowfall (335cm avg)',
      '20+ storm-ready operators',
      'Metro region coverage',
      'Newfoundland resilience and reliability'
    ]
  }
];

// Helper function to get city by slug
export const getCityBySlug = (slug: string): CityData | undefined => {
  return canadianCities.find(city => city.slug === slug);
};

// Helper function to get cities by province
export const getCitiesByProvince = (provinceCode: string): CityData[] => {
  return canadianCities.filter(city => city.provinceCode === provinceCode);
};

// Helper function to get all provinces
export const getAllProvinces = (): string[] => {
  const provinces = canadianCities.map(city => city.province);
  return [...new Set(provinces)];
};

// SEO metadata generator
export const generateCityMetadata = (city: CityData) => {
  return {
    title: `SNOWBLOWR in ${city.name} | On-Demand Snow Removal ${city.provinceCode}`,
    description: city.metaDescription,
    keywords: [
      `snow removal ${city.name}`,
      `${city.name} snow clearing`,
      `snow plow ${city.name}`,
      `driveway clearing ${city.name}`,
      `on-demand snow removal ${city.provinceCode}`,
      `SNOWBLOWR ${city.name}`,
      `${city.name} snow service`,
      `snow removal app ${city.name}`
    ].join(', '),
    openGraph: {
      title: `SNOWBLOWR in ${city.name} - On-Demand Snow Removal`,
      description: city.metaDescription,
      type: 'website',
      locale: 'en_CA',
      image: 'https://res.cloudinary.com/dtc6oth0i/image/upload/v1764387886/ChatGPT_Image_Nov_28_2025_10_44_12_PM_idoizu.png'
    }
  };
};

